import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { conversationApi } from '@formosa/shared/api/im';
import { startOrResumeSupportSession } from '@formosa/shared/utils/customerServiceEntry';
import { useChat } from '../chat/ChatProvider';

const MAX_VISIBLE = 3;

const MiniChatContext = createContext(null);

/**
 * Mini Chat surface manager — shares ChatProvider core; only window shell state here.
 * Call openMiniChat({ peerMemberId | conversationId | support: true, businessBinding? }).
 */
export function MiniChatProvider({ children }) {
  const { openConversation, dispatch } = useChat();
  const [slots, setSlots] = useState([]);
  const [overflow, setOverflow] = useState([]);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const resolveConversationId = useCallback(
    async ({ conversationId, peerMemberId, support, businessBinding }) => {
      if (support) {
        const session = await startOrResumeSupportSession({
          category: businessBinding?.category || 'GENERAL',
          language: businessBinding?.language,
          businessSystem: businessBinding?.businessSystem,
          businessType: businessBinding?.businessType,
          businessId: businessBinding?.businessId,
          priority: businessBinding?.priority,
        });
        const cid = String(session?.conversationId || '');
        if (!cid) {
          throw new Error('無法建立客服會話');
        }
        dispatch({
          type: 'UPSERT_CONVERSATION',
          conversation: {
            conversationId: cid,
            id: cid,
            conversationType: 'CUSTOMER_SERVICE',
            title: '線上客服',
            supportSessionId: session.sessionId,
            supportStatus: session.status,
          },
        });
        return { conversationId: cid, supportSession: session };
      }
      if (conversationId != null && String(conversationId) !== '') {
        return { conversationId: String(conversationId), supportSession: null };
      }
      if (peerMemberId == null || String(peerMemberId) === '') {
        throw new Error('缺少 conversationId、peerMemberId 或 support');
      }
      const conv = await conversationApi.createOrGetDirect(peerMemberId);
      const cid = String(conv?.conversationId || conv?.id || '');
      if (!cid) {
        throw new Error('無法建立私訊會話');
      }
      dispatch({ type: 'UPSERT_CONVERSATION', conversation: conv });
      return { conversationId: cid, supportSession: null };
    },
    [dispatch]
  );

  const placeInSlots = useCallback((cid, extra = {}) => {
    setSlots((prevSlots) => {
      const existingIdx = prevSlots.findIndex((s) => s.conversationId === cid);
      if (existingIdx >= 0) {
        const copy = [...prevSlots];
        const [item] = copy.splice(existingIdx, 1);
        setOverflow((prev) => prev.filter((id) => id !== cid));
        return [...copy, { ...item, ...extra, minimized: false }];
      }
      if (prevSlots.length < MAX_VISIBLE) {
        setOverflow((prev) => prev.filter((id) => id !== cid));
        return [...prevSlots, { conversationId: cid, minimized: false, ...extra }];
      }
      const [oldest, ...rest] = prevSlots;
      setOverflow((prev) => [
        oldest.conversationId,
        ...prev.filter((id) => id !== oldest.conversationId && id !== cid),
      ]);
      return [...rest, { conversationId: cid, minimized: false, ...extra }];
    });
  }, []);

  const openMiniChat = useCallback(
    async (opts = {}) => {
      setBusy(true);
      setError('');
      try {
        const resolved = await resolveConversationId(opts);
        const cid = resolved.conversationId;
        await openConversation(cid);
        placeInSlots(cid, {
          support: !!opts.support || !!resolved.supportSession,
          supportSessionId: resolved.supportSession?.sessionId || null,
          supportStatus: resolved.supportSession?.status || null,
        });
        setLauncherOpen(false);
        return cid;
      } catch (err) {
        setError(err?.message || '開啟迷你聊天失敗');
        throw err;
      } finally {
        setBusy(false);
      }
    },
    [openConversation, placeInSlots, resolveConversationId]
  );

  const closeMiniChat = useCallback((conversationId) => {
    const cid = String(conversationId);
    setSlots((prev) => prev.filter((s) => s.conversationId !== cid));
    setOverflow((prev) => prev.filter((id) => id !== cid));
  }, []);

  const minimizeMiniChat = useCallback((conversationId) => {
    const cid = String(conversationId);
    setSlots((prev) =>
      prev.map((s) => (s.conversationId === cid ? { ...s, minimized: true } : s))
    );
  }, []);

  const restoreMiniChat = useCallback((conversationId) => {
    const cid = String(conversationId);
    setSlots((prev) =>
      prev.map((s) => (s.conversationId === cid ? { ...s, minimized: false } : s))
    );
  }, []);

  const promoteFromOverflow = useCallback(
    async (conversationId) => {
      const cid = String(conversationId);
      await openConversation(cid);
      placeInSlots(cid);
      setLauncherOpen(false);
    },
    [openConversation, placeInSlots]
  );

  const value = useMemo(
    () => ({
      slots,
      overflow,
      maxVisible: MAX_VISIBLE,
      launcherOpen,
      setLauncherOpen,
      busy,
      error,
      setError,
      openMiniChat,
      closeMiniChat,
      minimizeMiniChat,
      restoreMiniChat,
      promoteFromOverflow,
    }),
    [
      slots,
      overflow,
      launcherOpen,
      busy,
      error,
      openMiniChat,
      closeMiniChat,
      minimizeMiniChat,
      restoreMiniChat,
      promoteFromOverflow,
    ]
  );

  return <MiniChatContext.Provider value={value}>{children}</MiniChatContext.Provider>;
}

export function useMiniChat() {
  const ctx = useContext(MiniChatContext);
  if (!ctx) {
    throw new Error('useMiniChat must be used within MiniChatProvider');
  }
  return ctx;
}
